import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router'
import type { ConfigDoNo, NoKind, SaidaDoNo } from '@tipscore/shared'
import {
  capacidadesDoCanal,
  condicoesSemEspera,
  esperasDepoisDeBotao,
  SAIDA_PADRAO,
  saidasDoNo,
} from '@tipscore/shared'
import {
  Background,
  type Connection,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useCallback, useState } from 'react'
import { BotaoAcao } from '../../components/BotaoAcao'
import { ArestaDoFluxo } from '../../components/fluxo/ArestaDoFluxo'
import { BLOCOS, type NomesDoFluxo } from '../../components/fluxo/blocos'
import { type DadosDoNo, NoDoFluxo } from '../../components/fluxo/NoDoFluxo'
import { type OpcoesDoFluxo, PainelDoBloco } from '../../components/fluxo/PainelDoBloco'
import { IconChevronLeft } from '../../components/icons'
import { Modal } from '../../components/Modal'
import { t } from '../../lib/i18n'
import { useTheme } from '../../lib/theme'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/automacoes/$id')({ component: EditorDeFluxo })

const TIPOS_DE_NO = { bloco: NoDoFluxo }
const TIPOS_DE_ARESTA = { fluxo: ArestaDoFluxo }

/** Id local de um bloco novo — o servidor aceita o que a tela mandar. */
function novoId() {
  return `nod_${crypto.randomUUID().replaceAll('-', '').slice(0, 20)}`
}

/**
 * O construtor de fluxo.
 *
 * Carrega o grafo inteiro, deixa mexer à vontade e grava tudo de uma vez —
 * substituição completa, não diferença incremental. O canvas mexe em blocos e
 * ligações ao mesmo tempo, e um salvamento parcial deixaria ligação apontando
 * para bloco que não existe mais. O grafo é pequeno, então reescrever custa
 * menos do que reconciliar.
 *
 * Nada é gravado sozinho: quem monta precisa poder arrastar, testar no olho e
 * desistir. O aviso de alteração pendente é o que impede sair achando que
 * salvou.
 */
function EditorDeFluxo() {
  const { id } = Route.useParams()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // O React Flow tem tema próprio para os controles e o minimapa. Sem dizer
  // qual é, eles nascem claros sobre o canvas escuro — dois retângulos brancos
  // no canto que denunciam componente de fora.
  const [tema] = useTheme()

  const fluxo = useQuery(trpc.automacao.get.queryOptions({ id }))
  const [nos, setNos, onNosChange] = useNodesState<Node<DadosDoNo>>([])
  const [arestas, setArestas, onArestasChange] = useEdgesState<Edge>([])
  const [selecionado, setSelecionado] = useState<string | null>(null)
  const [sujo, setSujo] = useState(false)
  const [carregou, setCarregou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const canal = fluxo.data?.canal
  const channelId = fluxo.data?.fluxo.channelId ?? ''

  const campanhas = useQuery({
    ...trpc.automacao.campanhas.queryOptions({ channelId }),
    enabled: Boolean(channelId),
  })
  const tags = useQuery(trpc.lead.tags.queryOptions())
  const equipe = useQuery(trpc.workspace.members.queryOptions())
  // Etapa é por inbox, e o inbox vem do canal do fluxo.
  const etapas = useQuery({
    ...trpc.inbox.stages.queryOptions({ inboxId: canal?.inboxId ?? '' }),
    enabled: Boolean(canal?.inboxId),
  })

  // Carrega uma vez. Sincronizar a cada render desfaria o que a pessoa está
  // arrastando no instante em que a consulta revalidasse.
  if (fluxo.data && !carregou) {
    setNos(
      fluxo.data.nos.map((no) => ({
        id: no.id,
        type: 'bloco',
        position: { x: no.posX, y: no.posY },
        data: { kind: no.kind as NoKind, config: no.config as ConfigDoNo, label: no.label },
      })),
    )
    setArestas(
      fluxo.data.arestas.map((aresta) => ({
        id: aresta.id,
        type: 'fluxo',
        source: aresta.fromNodeId,
        sourceHandle: aresta.saida,
        target: aresta.toNodeId,
      })),
    )
    setCarregou(true)
  }

  const salvar = useMutation({
    ...trpc.automacao.salvarGrafo.mutationOptions(),
    onSuccess: () => {
      setSujo(false)
      setErro(null)
      queryClient.invalidateQueries({ queryKey: [['automacao']] })
    },
    onError: (falha) => setErro(falha.message),
  })

  const definirStatus = useMutation({
    ...trpc.automacao.definirStatus.mutationOptions(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [['automacao']] }),
    onError: (falha) => setErro(falha.message),
  })

  /**
   * Liga uma porta a um bloco — **substituindo** o que já saía dali.
   *
   * Uma saída leva a um lugar só; é invariante do banco e é o que faz um fluxo
   * ser um fluxo em vez de duas execuções paralelas do mesmo contato. Sem a
   * substituição o `addEdge` empilha, o canvas mostra duas setas saindo do
   * mesmo ponto como se fosse possível, e a conta só chega na hora de salvar —
   * quando o índice único recusa e o trabalho parece perdido.
   *
   * Redesenhar por cima é o gesto natural de quem quer trocar o destino, e
   * agora ele funciona.
   */
  const conectar = useCallback(
    (ligacao: Connection) => {
      setArestas((atuais) => [
        ...atuais.filter(
          (aresta) =>
            !(
              aresta.source === ligacao.source &&
              (aresta.sourceHandle ?? SAIDA_PADRAO) === (ligacao.sourceHandle ?? SAIDA_PADRAO)
            ),
        ),
        {
          ...ligacao,
          id: `edg_${crypto.randomUUID()}`,
          type: 'fluxo',
        } as Edge,
      ])
      setSujo(true)
    },
    [setArestas],
  )

  function acrescentar(kind: NoKind) {
    const bloco = BLOCOS.find((linha) => linha.kind === kind)
    if (!bloco) return

    // Entra à direita do que já existe, para não nascer por baixo de outro.
    const x = nos.reduce((maior, no) => Math.max(maior, no.position.x), 0) + 280
    const novo: Node<DadosDoNo> = {
      id: novoId(),
      type: 'bloco',
      position: { x, y: 80 },
      data: { kind, config: bloco.padrao(), label: null },
    }
    setNos((atuais) => [...atuais, novo])
    setSelecionado(novo.id)
    setSujo(true)
  }

  /**
   * Duplica o bloco com a configuração junto.
   *
   * Sem as ligações de propósito: copiar um bloco é querer o **conteúdo** dele
   * noutro ponto do fluxo, e herdar as setas colocaria a cópia no meio do
   * caminho do original sem ninguém pedir.
   */
  const copiarNo = useCallback(
    (nodeId: string) => {
      setNos((atuais) => {
        const original = atuais.find((no) => no.id === nodeId)
        if (!original) return atuais
        // Um gatilho só por fluxo — duplicar seria criar um segundo começo.
        if (original.data.kind === 'gatilho') return atuais

        return [
          ...atuais,
          {
            ...original,
            id: novoId(),
            selected: false,
            position: { x: original.position.x + 40, y: original.position.y + 60 },
            data: {
              ...original.data,
              config: structuredClone(original.data.config),
              label: original.data.label ? `${original.data.label} (cópia)` : null,
            },
          },
        ]
      })
      setSujo(true)
    },
    [setNos],
  )

  function mudarNo(nodeId: string, parcial: Partial<DadosDoNo>) {
    /**
     * O bloco depois da mudança, calculado do estado atual — e **não** colhido
     * de dentro do `setNos`.
     *
     * O updater do `useState` não roda na hora: ele é guardado e chamado no
     * render seguinte. Uma variável preenchida lá dentro ainda estaria vazia
     * três linhas abaixo, e a poda simplesmente não aconteceria — sem erro,
     * sem aviso, só não funcionando.
     */
    const antes = nos.find((no) => no.id === nodeId)
    const depois = antes ? ({ ...antes.data, ...parcial } as DadosDoNo) : null

    setNos((atuais) =>
      atuais.map((no) => (no.id === nodeId ? { ...no, data: { ...no.data, ...parcial } } : no)),
    )

    /**
     * Mudar o bloco pode apagar uma porta, e a seta tem que sair junto.
     *
     * Apagar o segundo botão de uma mensagem, ou trocar a espera de "resposta"
     * para "tempo", faz sumir a porta de onde uma seta saía. Ela some do
     * desenho sozinha (não há mais `Handle` com aquele id), mas continuava no
     * estado e ia para o banco — e o próximo botão criado no índice 1
     * **herdava** aquele destino. Uma ligação que ninguém desenhou, invisível
     * até o dia em que o fluxo anda por ela.
     */
    if (depois) {
      const portas = new Set<string>(saidasDoNo(depois.kind, depois.config))
      setArestas((atuais) =>
        atuais.filter(
          (aresta) => aresta.source !== nodeId || portas.has(aresta.sourceHandle ?? SAIDA_PADRAO),
        ),
      )
    }

    setSujo(true)
  }

  function removerNo(nodeId: string) {
    setNos((atuais) => atuais.filter((no) => no.id !== nodeId))
    // As ligações dele saem junto: aresta órfã seria recusada no salvamento.
    setArestas((atuais) =>
      atuais.filter((aresta) => aresta.source !== nodeId && aresta.target !== nodeId),
    )
    setSelecionado(null)
    setSujo(true)
  }

  /**
   * Sair com alteração pendente perde o trabalho, e em silêncio.
   *
   * São dois caminhos diferentes e os dois precisam ser tapados: navegar dentro
   * do app (o roteador) e fechar a aba ou recarregar (o navegador). O segundo
   * só aceita o diálogo padrão do navegador — o texto é dele, não nosso.
   */
  const bloqueio = useBlocker({
    shouldBlockFn: () => sujo,
    enableBeforeUnload: () => sujo,
    withResolver: true,
  })

  function gravar() {
    salvar.mutate({
      id,
      /**
       * O par `kind`/`config` é estreitado aqui na fronteira.
       *
       * No estado do canvas os dois andam juntos mas são campos separados — a
       * paleta cria o config pelo `padrao()` do tipo e o painel só edita o
       * formato daquele tipo, então nunca se descolam. Quem garante de verdade
       * é o servidor, que valida bloco por bloco pelo `kind`: foi justamente a
       * falta disso que fez uma ação de nota ser gravada como mensagem.
       */
      nos: nos.map((no) => ({
        id: no.id,
        kind: no.data.kind,
        config: no.data.config,
        label: no.data.label,
        posX: Math.round(no.position.x),
        posY: Math.round(no.position.y),
      })) as Parameters<typeof salvar.mutate>[0]['nos'],
      arestas: arestas.map((aresta) => ({
        fromNodeId: aresta.source,
        saida: aresta.sourceHandle ?? 'then',
        toNodeId: aresta.target,
      })),
    })
  }

  /**
   * O botão de copiar entra no desenho aqui, e não no estado.
   *
   * Guardar a função dentro de `data` faria cada gravação mandar uma função
   * para o servidor e cada carregamento trazer um bloco sem ela. Injetar na
   * renderização mantém o estado com dado puro.
   */
  /**
   * Quem ninguém aponta.
   *
   * O gatilho fica de fora: ele é o começo, e não ter entrada é a definição
   * dele. Para o resto, não ter ninguém apontando quer dizer que o bloco nunca
   * vai executar — e a única pista disso no canvas seria a seta que **não**
   * está lá, que é o tipo de coisa que passa despercebida.
   */
  const alcancados = new Set(arestas.map((aresta) => aresta.target))

  /**
   * O link que inicia o fluxo, quando o gatilho é de `/start`.
   *
   * Montado aqui e não no nó porque depende do username do bot e da lista de
   * campanhas, que são dados da tela — o nó só desenha o que recebe.
   */
  function linkDoGatilho(dados: DadosDoNo): string | null {
    if (dados.kind !== 'gatilho') return null
    const cfg = dados.config as {
      gatilho?: string
      chave?: string | null
      campaignId?: string | null
    }
    if (cfg.gatilho !== 'start_command') return null

    if (cfg.campaignId) {
      return campanhas.data?.find((campanha) => campanha.id === cfg.campaignId)?.link ?? null
    }
    if (cfg.chave && canal?.botUsername) {
      return `https://t.me/${canal.botUsername}?start=${cfg.chave}`
    }
    return null
  }

  /**
   * Cliques por bloco, indexados pelo destino do botão.
   *
   * Consultado só aqui, e não dentro do painel: o painel é remontado a cada
   * clique num bloco, e uma consulta lá dentro refaria a busca toda vez que a
   * seleção mudasse — para um dado que é o mesmo do fluxo inteiro.
   */
  const cliques = useQuery(trpc.automacao.cliques.queryOptions({ id }))

  const cliquesPorBloco = new Map<
    string,
    Record<string, { cliques: number; pessoas: number; receberam: number; ctr: number }>
  >()
  for (const link of cliques.data ?? []) {
    if (!link.nodeId) continue
    const doBloco = cliquesPorBloco.get(link.nodeId) ?? {}
    doBloco[link.url] = {
      cliques: link.cliques,
      pessoas: link.pessoas,
      receberam: link.receberam,
      ctr: link.ctr,
    }
    cliquesPorBloco.set(link.nodeId, doBloco)
  }

  /**
   * Condições que vão comparar com a mensagem que iniciou o fluxo.
   *
   * A condição é um teste, não uma espera. Sem um bloco de espera por resposta
   * antes dela, ela roda no mesmo instante da mensagem anterior e o fluxo passa
   * direto — sem erro em lugar nenhum, que é o que torna o caso difícil de
   * achar sozinho.
   */
  const semEspera = new Set(
    condicoesSemEspera(
      nos.map((no) => ({ id: no.id, kind: no.data.kind, config: no.data.config })),
      arestas.map((aresta) => ({ fromNodeId: aresta.source, toNodeId: aresta.target })),
    ),
  )

  /**
   * Esperas por resposta logo depois de um botão de resposta.
   *
   * O toque no botão já é a resposta, e a espera fica aguardando outra: o
   * contato toca o botão e o fluxo para ali, sem erro em lugar nenhum.
   */
  const esperaDepoisDeBotao = new Set(
    esperasDepoisDeBotao(
      nos.map((no) => ({ id: no.id, kind: no.data.kind, config: no.data.config })),
      arestas.map((aresta) => ({
        fromNodeId: aresta.source,
        toNodeId: aresta.target,
        saida: (aresta.sourceHandle ?? SAIDA_PADRAO) as SaidaDoNo,
      })),
    ),
  )

  /**
   * Os nomes por trás dos ids, para cada bloco dizer "tag quente" em vez de
   * "uma tag". A etapa passa por `t()` aqui: é rótulo do sistema, e as outras
   * listas são dado de quem usa.
   */
  const nomes: NomesDoFluxo = {
    tags: new Map((tags.data ?? []).map((tag) => [tag.id, tag.label])),
    etapas: new Map((etapas.data ?? []).map((etapa) => [etapa.id, t(etapa.label)])),
    campanhas: new Map((campanhas.data ?? []).map((campanha) => [campanha.id, campanha.label])),
    pessoas: new Map((equipe.data ?? []).map((pessoa) => [pessoa.userId, pessoa.name])),
  }

  const nosDesenhados = nos.map((no) => ({
    ...no,
    data: {
      ...no.data,
      onCopiar: copiarNo,
      onRemover: removerNo,
      solto: no.data.kind !== 'gatilho' && !alcancados.has(no.id),
      condicaoSemEspera: semEspera.has(no.id),
      esperaDepoisDeBotao: esperaDepoisDeBotao.has(no.id),
      linkDeEntrada: linkDoGatilho(no.data),
      nomes,
      // O CTR de cada botão rastreado, para o bloco mostrar no rótulo.
      cliquesPorUrl: cliquesPorBloco.get(no.id),
    },
  }))

  const dadosSelecionados = nos.find((no) => no.id === selecionado)?.data
  const temGatilho = nos.some((no) => no.data.kind === 'gatilho')

  /**
   * O fluxo cabe no formulário simples?
   *
   * Cabe quando é exatamente o caso degenerado: um gatilho, uma mensagem e a
   * seta entre os dois. Qualquer bloco a mais e o formulário não teria onde
   * mostrá-lo — abrir ali apagaria o que não soubesse desenhar.
   */
  const cabeNoModoSimples =
    nos.length === 2 &&
    nos.some((no) => no.data.kind === 'gatilho') &&
    nos.some((no) => no.data.kind === 'mensagem') &&
    arestas.length === 1

  const opcoes: OpcoesDoFluxo = {
    campanhas: campanhas.data ?? [],
    tags: (tags.data ?? []).map((tag) => ({ id: tag.id, label: tag.label })),
    etapas: (etapas.data ?? []).map((etapa) => ({ id: etapa.id, label: etapa.label })),
    pessoas: (equipe.data ?? []).map((pessoa) => ({ id: pessoa.userId, name: pessoa.name })),
  }

  if (fluxo.isPending) {
    return <div className="p-6 text-xs text-ink-3">{t('Carregando o fluxo…')}</div>
  }

  if (!fluxo.data || !canal) {
    return <div className="p-6 text-xs text-danger">{t('Fluxo não encontrado.')}</div>
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-none flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <BotaoAcao
          onClick={() => navigate({ to: '/automacoes', search: { simples: undefined } })}
          className="flex items-center gap-1"
        >
          <IconChevronLeft className="size-3" />
          {t('Automações')}
        </BotaoAcao>

        <h1 className="min-w-0 flex-1 truncate font-display text-sm">{fluxo.data.fluxo.name}</h1>

        <span className="flex-none rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-ink-3">
          {fluxo.data.fluxo.status === 'ativo' ? t('no ar') : t(fluxo.data.fluxo.status)}
        </span>

        {sujo && (
          <span className="flex-none text-2xs font-semibold" style={{ color: 'var(--tag-amber)' }}>
            {t('alterações não salvas')}
          </span>
        )}

        {/* O modo simples é uma alternância, não outra porta de criação: ele
            só sabe desenhar gatilho ligado a uma mensagem, então some assim que
            o fluxo cresce. Oferecê-lo num fluxo ramificado seria prometer uma
            edição que perderia blocos.

            Sem checagem de alteração pendente aqui: isto é uma navegação, e o
            bloqueador já a intercepta com o diálogo lá de baixo. */}
        {cabeNoModoSimples && (
          <BotaoAcao onClick={() => navigate({ to: '/automacoes', search: { simples: id } })}>
            {t('Modo simples')}
          </BotaoAcao>
        )}

        <BotaoAcao onClick={gravar} disabled={salvar.isPending || !sujo}>
          {salvar.isPending ? t('Salvando…') : t('Salvar')}
        </BotaoAcao>

        <BotaoAcao
          variante={fluxo.data.fluxo.status === 'ativo' ? 'parada' : 'positiva'}
          disabled={definirStatus.isPending || (!temGatilho && fluxo.data.fluxo.status !== 'ativo')}
          onClick={() =>
            definirStatus.mutate({
              id,
              status: fluxo.data.fluxo.status === 'ativo' ? 'parado' : 'ativo',
            })
          }
        >
          {fluxo.data.fluxo.status === 'ativo' ? t('Parar') : t('Publicar')}
        </BotaoAcao>
      </header>
      {erro && (
        <p role="alert" className="flex-none bg-danger-soft px-4 py-2 text-2xs text-danger">
          {erro}
        </p>
      )}

      <div className="flex min-h-0 flex-1">
        {/* A paleta à esquerda, como no desenho de referência: escolher o bloco
            é o primeiro gesto, e ele não pode competir com o canvas. */}
        <nav className="flex w-44 flex-none flex-col gap-1.5 overflow-y-auto border-r border-line p-3">
          <span className="mb-1 text-2xs font-bold uppercase tracking-wider text-ink-3">
            {t('Blocos')}
          </span>
          {BLOCOS.map((bloco) => (
            <button
              key={bloco.kind}
              type="button"
              // Um gatilho só por fluxo: é por ele que todo mundo entra, e o
              // servidor recusa dois de qualquer forma.
              disabled={bloco.kind === 'gatilho' && temGatilho}
              onClick={() => acrescentar(bloco.kind)}
              className="rounded-md border border-line bg-surface px-2.5 py-2 text-left hover:border-line-strong hover:bg-surface-2 disabled:opacity-40"
            >
              <span className="flex items-center gap-1.5">
                <span className="size-2 flex-none rounded-full" style={{ background: bloco.cor }} />
                <span className="text-xs font-semibold">{t(bloco.titulo)}</span>
              </span>
              <span className="mt-0.5 block text-[10px] leading-snug text-ink-3">
                {t(bloco.descricao)}
              </span>
            </button>
          ))}

          {!temGatilho && (
            <p className="mt-2 rounded-md border border-dashed border-line-strong p-2 text-[10px] leading-relaxed text-ink-3">
              {t('Comece pelo gatilho — é por onde o contato entra.')}
            </p>
          )}
        </nav>

        <div className="min-w-0 flex-1">
          <ReactFlow
            nodes={nosDesenhados}
            edges={arestas}
            nodeTypes={TIPOS_DE_NO}
            edgeTypes={TIPOS_DE_ARESTA}
            defaultEdgeOptions={{ type: 'fluxo' }}
            onNodesChange={(mudancas) => {
              onNosChange(mudancas)
              // Só marca como sujo o que muda de verdade: seleção e medição
              // também passam por aqui, e marcariam alteração sem haver.
              if (mudancas.some((mudanca) => mudanca.type === 'position')) setSujo(true)
            }}
            onEdgesChange={(mudancas) => {
              onArestasChange(mudancas)
              if (mudancas.some((mudanca) => mudanca.type === 'remove')) setSujo(true)
            }}
            onConnect={conectar}
            /**
             * Um bloco não liga na própria entrada.
             *
             * Arrastar da "próxima etapa" de volta para o mesmo bloco é fácil
             * de fazer sem querer, e o resultado é um laço de um nó só — o
             * motor pararia nele pelo teto de passos, mas já teria mandado a
             * mesma mensagem cinco vezes para o cliente antes disso.
             */
            isValidConnection={(ligacao) => ligacao.source !== ligacao.target}
            onNodeClick={(_evento, no) => setSelecionado(no.id)}
            onPaneClick={() => setSelecionado(null)}
            fitView
            colorMode={tema}
            // Raio de encaixe generoso: junto com a área que cobre o bloco,
            // soltar perto já basta. O padrão de 20px exige mira que ninguém
            // acerta num canvas com zoom.
            connectionRadius={60}
            proOptions={{ hideAttribution: false }}
            className="bg-paper"
          >
            <Background gap={18} size={1} color="var(--line)" />
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              // Menor do que o padrão: um fluxo honesto tem dez blocos, e o
              // minimapa cheio cobria justamente o canto onde eles terminam.
              style={{ width: 140, height: 90 }}
              maskColor="color-mix(in srgb, var(--paper) 70%, transparent)"
              nodeColor="var(--line-strong)"
              className="!bg-surface-2"
            />
          </ReactFlow>
        </div>

        {/* Sair com alteração pendente perde o trabalho em silêncio. O diálogo
            é do app, e não o `confirm` do navegador: aquele ignora a paleta, não
            dá para escrever "descartar" no botão e trava a página inteira. */}
        {bloqueio.status === 'blocked' && (
          <Modal
            title={t('Sair sem salvar?')}
            description={t('As alterações deste fluxo ainda não foram gravadas.')}
            onClose={bloqueio.reset}
          >
            <div className="flex flex-col gap-3 p-4">
              <p className="text-xs leading-relaxed text-ink-2">
                {t(
                  'Você mexeu no fluxo e não salvou. Sair agora descarta o que foi montado desde a última gravação.',
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    gravar()
                    bloqueio.reset()
                  }}
                  className="rounded-md bg-accent px-3 py-2 text-xs font-semibold text-on-accent"
                >
                  {t('Salvar e ficar')}
                </button>
                <BotaoAcao variante="destrutiva" onClick={bloqueio.proceed}>
                  {t('Descartar e sair')}
                </BotaoAcao>
                <BotaoAcao onClick={bloqueio.reset}>{t('Continuar editando')}</BotaoAcao>
              </div>
            </div>
          </Modal>
        )}

        {dadosSelecionados && selecionado && (
          <PainelDoBloco
            /**
             * Remonta ao trocar de bloco.
             *
             * O painel guarda estado próprio que nasce do bloco selecionado — a
             * unidade do campo de duração, a identidade das linhas de botão.
             * Sem a `key`, escolher outro bloco reaproveita a instância e esse
             * estado atravessa junto: a unidade do bloco anterior fica, e o
             * valor do novo aparece convertido errado.
             */
            key={selecionado}
            dados={{
              ...dadosSelecionados,
              cliquesPorUrl: cliquesPorBloco.get(selecionado),
            }}
            capacidades={capacidadesDoCanal(canal.tipo)}
            opcoes={opcoes}
            botUsername={canal.botUsername}
            onMudar={(parcial) => mudarNo(selecionado, parcial)}
            onRemover={() => removerNo(selecionado)}
            onFechar={() => setSelecionado(null)}
          />
        )}
      </div>
    </div>
  )
}
